import QueryString, { stringify as qsStringify } from 'qs';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';

function stringifyChanged<T extends Record<string, any>>(
  object: T,
  initialObject: T,
  options: QueryString.IStringifyOptions<QueryString.BooleanOptional> | undefined = undefined
): string {
  return qsStringify(
    Object.fromEntries(Object.entries(object).filter(([key, value]) => !isEqual(initialObject[key], value))),
    options
  );
}

export function stringifyChangedFilters<T extends Record<string, any>>(object: T, initialObject: T): string {
  return stringifyChanged(object, initialObject, { arrayFormat: 'comma', commaRoundTrip: true });
}

export function stringifyFilters<T extends Record<string, any>>(object: T): string {
  return qsStringify(object, { arrayFormat: 'comma', commaRoundTrip: true });
}

export function parseFilters<T extends Record<string, string | number | boolean | (string | number | boolean)[]>>(
  queryString: string | undefined
): T {
  if (!queryString) return {} as T;

  const params = new URLSearchParams(queryString);

  const parsedParams: Record<string, string | number | boolean | (string | number | boolean)[]> = {};
  for (const [key, value] of params) {
    if ((typeof value === 'string' && value.includes(',')) || key.endsWith('[]')) {
      parsedParams[key.replace('[]', '')] = value.split(',').map(parseValue);
    } else {
      parsedParams[key] = parseValue(value);
    }
  }

  return parsedParams as T;
}

export function parseAndApplyFilters<
  T extends Record<string, string | number | boolean | (string | number | boolean)[]>,
>(queryString: string, existingObject: T) {
  const parsedParams = parseFilters<T>(queryString);
  const rawParams = new URLSearchParams(queryString);

  for (const key in parsedParams) {
    if (existingObject.hasOwnProperty(key)) {
      // A free-text filter keeps what was typed: split on its commas or read as a number, a pasted
      // list of EANs would reach the API as an array the endpoint cannot bind.
      existingObject[key] = (
        typeof existingObject[key] === 'string' ? (rawParams.get(key) ?? '') : parsedParams[key]
      ) as T[typeof key];
    }
  }

  return parsedParams;
}

function parseValue(value: string): string | number | boolean {
  if (value === 'true') return true;
  if (value === 'false') return false;

  const numValue = Number(value);
  if (!isNaN(numValue)) return numValue;

  return value;
}

export function getProductPageFiltersFromOverview(queryString: string): string | undefined {
  const initialFilters = {
    seasonIds: [],
    statusIds: [],
    tagIds: [],
    supplierIds: [],
    sortingSortedDescending: false,
    sorting: undefined,
  };

  const productPageFilters = cloneDeep(initialFilters);

  parseAndApplyFilters<{
    seasonIds?: number[];
    statusIds?: number[];
    tagIds?: number[];
    supplierIds?: number[];
    sortingSortedDescending?: boolean;
    sorting?: number;
  }>(queryString, productPageFilters);

  const allowedSortingValues = [
    0, // ArticleNumber
    14, // PrincipalMaterialType
    2, // ColorName
    5, // Comment
    8, // Gender
    9, // Images
    21, // Seasons,
    16, // Status
    20, // SupplierName
    23, // Tags
    10, // TargetPrice
    11, // FactoryPrice
    12, // RetailPrice
    28, // CountryOfOrigin
    29, // Size
  ];

  if (productPageFilters.sorting && !allowedSortingValues.includes(productPageFilters.sorting)) {
    delete productPageFilters.sorting;
  }

  const stringifiedFilters = stringifyChangedFilters(productPageFilters, initialFilters);

  return stringifiedFilters !== '' ? stringifiedFilters : undefined;
}
